import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardBody, Col, Container, Input, Label, Row, Button, Form, FormFeedback, Alert, Spinner } from 'reactstrap';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

//redux
import { useSelector, useDispatch } from "react-redux";

import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// actions
import { loginUser, socialLogin, resetLoginFlag, googleLogin, microsoftLogin, githubLogin } from "../../slices/thunks";

// OAuth hooks
import useGoogleAuth from "../../hooks/useGoogleAuth";
import useMicrosoftAuth from "../../hooks/useMicrosoftAuth";
import useGitHubAuth from "../../hooks/useGitHubAuth";

// Google Analytics tracking
import { trackLoginAttempt, trackLoginSuccess, trackLoginError, trackOAuthLogin } from "../../helpers/analytics";

// Logo is now loaded from public folder
import { createSelector } from 'reselect';
//import images

// Move selector outside component to maintain memoization
// Use multiple input selectors to only track specific state slices
const loginpageData = createSelector(
    (state: any) => state.Account?.user,
    (state: any) => state.Login?.error,
    (state: any) => state.Login?.loading,
    (state: any) => state.Login?.errorMsg,
    (user, error, loading, errorMsg) => ({
        user,
        error,
        loading,
        errorMsg,
    })
);

const Login = (props: any) => {
    const dispatch: any = useDispatch();
    // Inside your component
    const {
        user, error, errorMsg
    } = useSelector(loginpageData);

    console.log("user data in login page:", user);
    
    const [userLogin, setUserLogin] = useState<any>([]);
    const [passwordShow, setPasswordShow] = useState<boolean>(false);
    const [loader, setLoader] = useState<boolean>(false);
    const [googleButtonContainer, setGoogleButtonContainer] = useState<HTMLDivElement | null>(null);
    const googleButtonRendered = useRef(false);

    useEffect(() => {
        if (!user) {
            return;
        }

        if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
            const firebaseEmail = user?.multiFactor?.user?.email;
            if (firebaseEmail) {
                setUserLogin({
                    email: firebaseEmail,
                    password: ''
                });
            }
            return;
        }

        const backendUser = user?.user;
        if (backendUser && backendUser.email) {
            setUserLogin({
                email: backendUser.email,
                password: backendUser.confirm_password || ''
            });
        }
    }, [user]);

    // Google OAuth configuration
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

    // Memoize callbacks to prevent infinite re-renders
    const handleGoogleSuccess = useCallback((response: any) => {
        console.log('Google login success:', response);

        // Track OAuth login (GDPR/CCPA compliant - no PII)
        trackOAuthLogin('google');

        setLoader(true);
        dispatch(googleLogin(response.credential, props.router.navigate));
    }, [dispatch, props.router.navigate]);

    const handleGoogleError = useCallback((error: any) => {
        console.error('Google login error:', error);
        dispatch(resetLoginFlag());
        setLoader(false);
    }, [dispatch]);

    const { isLoaded: isGoogleLoaded, renderButton, error: googleError } = useGoogleAuth({
        clientId,
        onSuccess: handleGoogleSuccess,
        onError: handleGoogleError,
        autoSelect: false,
    });

    // Microsoft OAuth configuration
    const microsoftClientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID || '';

    const handleMicrosoftSuccess = useCallback((response: any) => {
        console.log('Microsoft login success:', response);

        // Track OAuth login (GDPR/CCPA compliant - no PII)
        trackOAuthLogin('microsoft');

        setLoader(true);
        dispatch(microsoftLogin(response, props.router.navigate));
    }, [dispatch, props.router.navigate]);

    const handleMicrosoftError = useCallback((error: any) => {
        console.error('Microsoft login error:', error);
        dispatch(resetLoginFlag());
        setLoader(false);
    }, [dispatch]);

    const {
        isLoaded: isMicrosoftLoaded,
        signIn: microsoftSignIn,
        error: microsoftError
    } = useMicrosoftAuth({
        clientId: microsoftClientId,
        onSuccess: handleMicrosoftSuccess,
        onError: handleMicrosoftError,
    });

    // GitHub OAuth configuration
    const githubClientId = process.env.REACT_APP_GITHUB_CLIENT_ID || '';

    const handleGitHubSuccess = useCallback((response: any) => {
        console.log('GitHub login success:', response);

        // Track OAuth login
        trackOAuthLogin('github');

        setLoader(true);
        dispatch(githubLogin(response, props.router.navigate));
    }, [dispatch, props.router.navigate]);

    const handleGitHubError = useCallback((error: any) => {
        console.error('GitHub login error:', error);
        dispatch(resetLoginFlag());
        setLoader(false);
    }, [dispatch]);

    const {
        isLoaded: isGitHubLoaded,
        signIn: githubSignIn,
        error: githubError
    } = useGitHubAuth({
        clientId: githubClientId,
        onSuccess: handleGitHubSuccess,
        onError: handleGitHubError,
    });

    // Render Google button when container is ready and Google is loaded
    useEffect(() => {
        if (googleButtonContainer && isGoogleLoaded && !googleError && !googleButtonRendered.current) {
            renderButton(googleButtonContainer, {
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                shape: 'rectangular',
                width: '100%',
            });
            googleButtonRendered.current = true;
        }
    }, [googleButtonContainer, isGoogleLoaded, renderButton, googleError]);

    const validation: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            email: userLogin.email || '',
            password: userLogin.password || '',
        },
        validationSchema: Yup.object({
            email: Yup.string().required("Please Enter Your Email"),
            password: Yup.string().required("Please Enter Your Password"),
        }),
        onSubmit: (values) => {
            // Track login attempt (GDPR/CCPA compliant - no PII)
            trackLoginAttempt();

            dispatch(loginUser(values, props.router.navigate));
            setLoader(true)
        }
    });

    const signIn = (type: any) => {
        dispatch(socialLogin(type, props.router.navigate));
    };


    //for facebook and google authentication
    const socialResponse = (type: any) => {
        signIn(type);
    };


    // Track successful login (GDPR/CCPA compliant - only user_id, no email/name)
    useEffect(() => {
        if (user && user.user) {
            trackLoginSuccess(
                user.user.id || user.user.userId || user.user.email
            );
        }
    }, [user]);

    // Track login error (GDPR/CCPA compliant - no email)
    useEffect(() => {
        if (errorMsg) {
            // Track login error (no PII)
            trackLoginError(errorMsg);

            // Stop loader immediately when error occurs
            setLoader(false);
            // Clear error after 5 seconds
            const timer = setTimeout(() => {
                dispatch(resetLoginFlag());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dispatch, errorMsg]);

    document.title = "Sign In | Integr Dev";

    return (
        <React.Fragment>
            {/*<ParticlesAuth>*/}
                <div className="auth-page-content" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-3">
                                    <div>
                                        <Link to="/" className="d-inline-block auth-logo">
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="ri-sparkling-line" style={{ fontSize: '40px', color: '#7F13EC' }}></i>
                                                <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#7F13EC' }}>Integr.Dev</span>
                                            </div>
                                        </Link>
                                    </div>
                                    {/*<p className="mt-3 fs-15 fw-medium">Integr Dev Portal</p>*/}
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={8} lg={6} xl={5}>
                                <Card className="mt-4" style={{ border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                    <CardBody className="p-4">
                                        <div className="text-center mt-2">
                                            <h5 className="text-primary">Welcome Back !</h5>
                                            <p className="text-muted">Sign in to continue to Integr Dev.</p>
                                        </div>
                                        {error && error ? (<Alert color="danger"> {error} </Alert>) : null}
                                        <div className="p-2 mt-4">
                                            <Form
                                                onSubmit={(e: any) => {
                                                    e.preventDefault();
                                                    validation.handleSubmit();
                                                    return false;
                                                }}
                                                action="#">

                                                <div className="mb-3">
                                                    <Label htmlFor="email" className="form-label">Email</Label>
                                                    <Input
                                                        name="email"
                                                        className="form-control"
                                                        placeholder="Enter email"
                                                        type="email"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.email || ""}
                                                        invalid={
                                                            validation.touched.email && validation.errors.email ? true : false
                                                        }
                                                    />
                                                    {validation.touched.email && validation.errors.email ? (
                                                        <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                                                    ) : null}
                                                </div>

                                                <div className="mb-3">
                                                    <div className="float-end">
                                                        <Link to="/forgot-password" className="text-muted">Forgot password?</Link>
                                                    </div>
                                                    <Label className="form-label" htmlFor="password-input">Password</Label>
                                                    <div className="position-relative auth-pass-inputgroup mb-3">
                                                        <Input
                                                            name="password"
                                                            value={validation.values.password || ""}
                                                            type={passwordShow ? "text" : "password"}
                                                            className="form-control pe-5"
                                                            placeholder="Enter Password"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            invalid={
                                                                validation.touched.password && validation.errors.password ? true : false
                                                            }
                                                        />
                                                        {validation.touched.password && validation.errors.password ? (
                                                            <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                                                        ) : null}
                                                        <button className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted" type="button" onClick={() => setPasswordShow(!passwordShow)} id="password-addon"><i className="ri-eye-fill align-middle"></i></button>
                                                    </div>
                                                </div>

                                                <div className="form-check">
                                                    <Input className="form-check-input" type="checkbox" value="" id="auth-remember-check" />
                                                    <Label className="form-check-label" htmlFor="auth-remember-check">Remember me</Label>
                                                </div>

                                                <div className="mt-4">
                                                    <Button color="success"
                                                        disabled={loader && true}
                                                        className="btn btn-success w-100" type="submit">
                                                        {loader && <Spinner size="sm" className='me-2'> Loading... </Spinner>}
                                                        Sign In
                                                    </Button>
                                                </div>

                                                <div className="mt-4 text-center">
                                                    <div className="signin-other-title">
                                                        <h5 className="fs-13 mb-4 title">Or sign in with</h5>
                                                    </div>
                                                    
                                                    {/* Google Sign-In Button */}
                                                    <div className="mb-3">
                                                        {googleError ? (
                                                            <Alert color="warning" className="mb-3">
                                                                <small>{googleError}</small>
                                                            </Alert>
                                                        ) : (
                                                            <div
                                                                ref={setGoogleButtonContainer}
                                                                style={{ minHeight: '44px' }}
                                                            >
                                                                {!isGoogleLoaded && (
                                                                    <Button color="light" disabled className="w-100">
                                                                        <Spinner size="sm" className="me-2" />
                                                                        Loading Google Sign-In...
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Microsoft Login Button */}
                                                    <div className="mb-3">
                                                        {(microsoftError || (error && errorMsg)) ? (
                                                            <Alert color="warning" className="mb-3">
                                                                <small>{microsoftError || error}</small>
                                                            </Alert>
                                                        ) : null}
                                                        <Button
                                                            color="primary"
                                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (isMicrosoftLoaded && !loader) {
                                                                    microsoftSignIn();
                                                                }
                                                            }}
                                                            disabled={!isMicrosoftLoaded || loader}
                                                            title="Sign in with Microsoft"
                                                        >
                                                            {!isMicrosoftLoaded ? (
                                                                <Spinner size="sm" className="me-2" />
                                                            ) : (
                                                                <i className="ri-microsoft-fill fs-16"></i>
                                                            )}
                                                            {!isMicrosoftLoaded ? 'Loading Microsoft Sign-In...' : 'Sign in with Microsoft'}
                                                        </Button>
                                                    </div>

                                                    {/* GitHub Login Button */}
                                                    <div className="mb-3">
                                                        {githubError ? (
                                                            <Alert color="warning" className="mb-3">
                                                                <small>{githubError}</small>
                                                            </Alert>
                                                        ) : null}
                                                        <Button
                                                            color="dark"
                                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (isGitHubLoaded && !loader) {
                                                                    githubSignIn();
                                                                }
                                                            }}
                                                            disabled={!isGitHubLoaded || loader}
                                                            title="Sign in with GitHub"
                                                        >
                                                            {!isGitHubLoaded ? (
                                                                <Spinner size="sm" className="me-2" />
                                                            ) : (
                                                                <i className="ri-github-fill fs-16"></i>
                                                            )}
                                                            {!isGitHubLoaded ? 'Loading GitHub Sign-In...' : 'Sign in with GitHub'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Form>
                                        </div>
                                    </CardBody>
                                </Card>

                                <div className="mt-4 text-center">
                                    <p className="mb-0">Don't have an account ? <Link to="/register" className="fw-semibold text-primary text-decoration-underline"> Signup </Link> </p>
                                </div>

                            </Col>
                        </Row>

                        {/* Copyright */}
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-5 mb-4">
                                    <p className="mb-0 text-muted">© {new Date().getFullYear()} Integr Dev. All rights reserved.</p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            {/*</ParticlesAuth>*/}
        </React.Fragment>
    );
};

export default withRouter(Login);
